import { lazy, Suspense, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { leaderboardApi, type LeaderboardEntry, type LeaderboardWeights } from '../api/leaderboard'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { Spinner } from '../components/Spinner'

const Podium3D = lazy(() => import('../components/Podium3D'))

type Tab = 'week' | 'month' | 'all'
const TABS: [Tab, string][] = [
  ['week', 'Semana'],
  ['month', 'Mensal'],
  ['all', 'Geral'],
]
const MEDAL_EMOJI = ['🥇', '🥈', '🥉']
const MEDAL_BG = ['bg-amber-50', 'bg-slate-100', 'bg-orange-50']

export default function Leaderboard() {
  const [tab, setTab] = useState<Tab>('week')
  const [month, setMonth] = useState<'current' | 'last'>('current')

  const { data, isLoading } = useQuery({
    queryKey: ['leaderboard', tab, tab === 'month' ? month : ''],
    queryFn: () => leaderboardApi.get(tab, tab === 'month' ? month : undefined),
  })
  const entries = data?.entries ?? []
  const top3 = entries.slice(0, 3)
  const hasResults = data?.hasResults ?? false

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Placar 🏆</h1>
        <div className="inline-flex rounded-lg border border-slate-300 p-0.5">
          {TABS.map(([t, l]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                tab === t ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-500">{data?.label ?? '—'}</span>
        {tab === 'month' && (
          <div className="inline-flex rounded-lg border border-slate-300 p-0.5">
            {(
              [
                ['current', 'Mês atual'],
                ['last', 'Mês passado'],
              ] as ['current' | 'last', string][]
            ).map(([m, l]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMonth(m)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  month === m ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum gerente com pontuação neste período ainda.
        </p>
      ) : (
        <>
          {/* Pódio imersivo (3D) */}
          <div className="relative h-[380px] overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-lg">
            <ErrorBoundary fallback={<PodiumFlat top3={top3} />}>
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Carregando cena 3D…
                  </div>
                }
              >
                <Podium3D top3={top3} />
              </Suspense>
            </ErrorBoundary>
            <div className="pointer-events-none absolute bottom-2 right-3 text-[10px] text-slate-500">
              arraste para girar
            </div>
          </div>

          {/* Regras de pontuação — claras e visíveis */}
          <RulesCard tab={tab} weights={data?.weights} />

          {/* Dados brutos — ranking completo com todos os números isolados */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-3 py-2 font-semibold">#</th>
                  <th className="px-3 py-2 font-semibold">Gerente</th>
                  <th className="px-3 py-2 text-right font-semibold">Dias ativos</th>
                  <th className="px-3 py-2 text-right font-semibold">Ações resolvidas</th>
                  <th className="px-3 py-2 text-right font-semibold">Follow-through</th>
                  <th
                    className="px-3 py-2 text-right font-semibold"
                    title="Contribuições que ensinaram a IA: respostas, fatos confirmados/escritos, motivos e comentários nas ações, achados"
                  >
                    Conhecimento
                  </th>
                  {hasResults && (
                    <th className="px-3 py-2 text-right font-semibold">Vitórias</th>
                  )}
                  <th className="px-3 py-2 text-right font-semibold">Pontos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e, i) => (
                  <tr key={e.managerId} className={i < 3 ? MEDAL_BG[i] : undefined}>
                    <td className="px-3 py-2 font-semibold text-slate-500">
                      {i < 3 ? MEDAL_EMOJI[i] : i + 1}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800">{e.managerName}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {e.daysActive}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {e.resolved}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {e.followThroughPct == null ? '—' : `${e.followThroughPct}%`}
                      <span className="ml-1 text-[11px] text-slate-400">
                        ({e.resolvedEligible}/{e.eligible})
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-violet-600">
                      {e.knowledge ?? 0}
                    </td>
                    {hasResults && (
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600">
                        {e.wins ?? 0}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right tabular-nums text-lg font-bold text-slate-900">
                      {e.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// Regras de pontuação — visíveis, adaptadas à aba ativa (pesos configuráveis).
function RulesCard({ tab, weights }: { tab: Tab; weights?: LeaderboardWeights }) {
  const w = weights ?? {
    day: 12,
    followMaxWeek: 20,
    followMaxMonth: 40,
    allWin: 15,
    knowledge: 2,
    knowledgeMaxWeek: 20,
    knowledgeMaxMonth: 60,
  }
  const followMax = tab === 'week' ? w.followMaxWeek : w.followMaxMonth
  const knowledgeMax = tab === 'week' ? w.knowledgeMaxWeek : w.knowledgeMaxMonth
  const knowledgeRule =
    'por contribuição que ensina a IA sobre o cliente — responder pergunta, confirmar/corrigir um ' +
    'aprendizado, escrever um fato, dar motivo ou comentário numa ação, responder investigação'
  const rules =
    tab === 'all'
      ? [
          { w: 'Σ meses', t: 'soma da pontuação de cada mês (dias ativos + follow-through + conhecimento do mês)' },
          { w: `×${w.day}`, t: 'por dia ativo em cada mês' },
          { w: `até +${w.followMaxMonth}`, t: 'bônus de follow-through por mês' },
          { w: `×${w.knowledge}`, t: `${knowledgeRule} (até +${w.knowledgeMaxMonth} por mês)` },
          { w: `×${w.allWin}`, t: 'por vitória — ação concluída que melhorou o KPI-alvo depois' },
        ]
      : [
          {
            w: `×${w.day}`,
            t: 'por dia ativo — dia em que usou a ferramenta (marcar ação, gerar/ler relatório, responder a IA ou conversar com ela)',
          },
          {
            w: `até +${followMax}`,
            t: 'bônus de follow-through — % das ações dos seus clientes que foram resolvidas',
          },
          { w: `×${w.knowledge}`, t: `${knowledgeRule} (até +${knowledgeMax} no período)` },
        ]
  const suffix = tab === 'all' ? 'Geral' : tab === 'month' ? 'Mensal' : 'Semana'
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
        <span>📐</span> Como pontuamos — {suffix}
      </h2>
      <ul className="space-y-2">
        {rules.map((r) => (
          <li key={r.w} className="flex items-start gap-2.5 text-sm text-slate-600">
            <span className="mt-0.5 inline-flex min-w-[56px] justify-center rounded-md bg-brand-100 px-2 py-0.5 text-xs font-bold text-brand-700">
              {r.w}
            </span>
            <span>{r.t}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
        Engajamento conta para <strong>quem usou</strong> a ferramenta; follow-through e vitórias
        contam pelos <strong>clientes do gerente</strong>. Vitórias são{' '}
        <strong>correlação, não prova</strong> — vários fatores atuam na mesma semana.
      </p>
    </div>
  )
}

// Fallback 2D (menos de 3 no pódio, WebGL indisponível ou erro na cena 3D).
function PodiumFlat({ top3 }: { top3: LeaderboardEntry[] }) {
  const order = [1, 0, 2] // 2º, 1º, 3º da esquerda p/ direita
  return (
    <div className="flex h-full items-end justify-center gap-4 p-6">
      {order.map((rank) => {
        const e = top3[rank]
        if (!e) return <div key={rank} className="w-28" />
        const h = rank === 0 ? 'h-40' : rank === 1 ? 'h-28' : 'h-24'
        return (
          <div key={e.managerId} className="flex w-28 flex-col items-center">
            <div className="text-3xl">{MEDAL_EMOJI[rank]}</div>
            <div className="text-sm font-bold text-white">{e.managerName}</div>
            <div className="mb-1 text-xl font-extrabold text-amber-300">{e.points}</div>
            <div
              className={`w-full rounded-t-lg bg-gradient-to-b from-slate-600 to-slate-800 ${h}`}
            />
          </div>
        )
      })}
    </div>
  )
}
